import React, { useState } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

export default function Analytics() {
  const [dateRange, setDateRange] = useState('7d');

  // Sample data
  const engagementData = [
    { date: 'Mon', sent: 450, opened: 380, clicked: 120 },
    { date: 'Tue', sent: 520, opened: 410, clicked: 140 },
    { date: 'Wed', sent: 480, opened: 390, clicked: 135 },
    { date: 'Thu', sent: 550, opened: 450, clicked: 160 },
    { date: 'Fri', sent: 600, opened: 520, clicked: 180 },
    { date: 'Sat', sent: 420, opened: 350, clicked: 110 },
    { date: 'Sun', sent: 380, opened: 320, clicked: 95 },
  ];

  const deviceData = [
    { name: 'iOS', value: 45, color: '#3B82F6' },
    { name: 'Android', value: 38, color: '#10B981' },
    { name: 'Web', value: 17, color: '#F59E0B' },
  ];

  const quizPerformance = [
    { quiz: 'History', attempted: 320, completed: 280, avgScore: 75 },
    { quiz: 'Geography', attempted: 290, completed: 250, avgScore: 68 },
    { quiz: 'Polity', attempted: 350, completed: 310, avgScore: 82 },
    { quiz: 'Economy', attempted: 280, completed: 230, avgScore: 71 },
    { quiz: 'Science', attempted: 310, completed: 270, avgScore: 79 },
  ];

  const metrics = [
    {
      title: 'Open Rate',
      value: '84.5%',
      change: '+2.4%',
      trend: 'up',
      color: 'text-green-600',
    },
    {
      title: 'Click Rate',
      value: '32.8%',
      change: '+5.1%',
      trend: 'up',
      color: 'text-green-600',
    },
    {
      title: 'Quiz Completion',
      value: '67.3%',
      change: '-1.2%',
      trend: 'down',
      color: 'text-red-600',
    },
    {
      title: 'Avg. Response Time',
      value: '2.4m',
      change: '-18s',
      trend: 'up',
      color: 'text-green-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track notification performance and user engagement
          </p>
        </div>

        {/* Date range selector */}
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1">
          {['24h', '7d', '30d', '90d'].map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                dateRange === range
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric) => (
          <div
            key={metric.title}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {metric.title}
              </p>
              {metric.trend === 'up' ? (
                <ArrowTrendingUpIcon className={`w-5 h-5 ${metric.color}`} />
              ) : (
                <ArrowTrendingDownIcon className={`w-5 h-5 ${metric.color}`} />
              )}
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {metric.value}
              </p>
              <span className={`text-sm font-medium ${metric.color}`}>
                {metric.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Engagement Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Notification Engagement
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={engagementData}>
            <CartesianGrid strokeDasharray="3 3" className="dark:opacity-20" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="sent"
              stackId="1"
              stroke="#3B82F6"
              fill="#3B82F6"
              fillOpacity={0.2}
            />
            <Area
              type="monotone"
              dataKey="opened"
              stackId="2"
              stroke="#10B981"
              fill="#10B981"
              fillOpacity={0.2}
            />
            <Area
              type="monotone"
              dataKey="clicked"
              stackId="3"
              stroke="#F59E0B"
              fill="#F59E0B"
              fillOpacity={0.2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Device Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={deviceData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {deviceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Quiz Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quiz Performance
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={quizPerformance}>
              <CartesianGrid strokeDasharray="3 3" className="dark:opacity-20" />
              <XAxis dataKey="quiz" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="avgScore" fill="#8B5CF6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Notifications Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Top Performing Notifications
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Open Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Click Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[
                { title: 'Daily Quiz Ready!', type: 'Quiz', sent: 1250, openRate: 92, clickRate: 45 },
                { title: 'New Study Material', type: 'Content', sent: 980, openRate: 88, clickRate: 38 },
                { title: 'Achievement Unlocked', type: 'Achievement', sent: 750, openRate: 95, clickRate: 52 },
                { title: 'Weekly Progress', type: 'Report', sent: 620, openRate: 78, clickRate: 28 },
              ].map((notification, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {notification.title}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {notification.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {notification.sent.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {notification.openRate}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {notification.clickRate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}